import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { LanguageDrawer } from '../../../components/LanguageDrawer';
import { LANGUAGE_NAMES } from '../../../utils/languageLabels';
import { EditChevron } from '../../userInfo/ui/EditChevron';
import { useTelegramBackButton } from '../../../utils/BackButton';
import { selectionChanged } from '../../../utils/hapticEvents';
import {
  usePushSettings,
  useSavePushSettings,
} from '../services/pushSettings';
import { PushChannel } from '../types/PushSettings';

/**
 * Профиль → Настройки: язык и каналы уведомлений.
 *
 * Своей кнопки «назад» здесь нет — приложение живёт в Telegram, и назад ведёт
 * системная кнопка мини-аппа (useTelegramBackButton).
 */
export const Settings = () => {
  useTelegramBackButton('/profile');

  const { t } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem('language') || 'uz');

  const { settings, isLoading } = usePushSettings();
  const { savePushSettings, isSaving } = useSavePushSettings();

  // Переключатель должен отзываться сразу, не дожидаясь ответа сервера.
  // Черновик перекрывает серверное значение до конца запроса; при отказе
  // откатываем его обратно, и строка возвращается в прежнее положение.
  const [draft, setDraft] = useState<typeof settings | null>(null);
  const current = draft ?? settings;

  const channels: { key: PushChannel; label: string }[] = [
    { key: 'push_notification', label: t('notifications-push') },
    { key: 'tg_notification', label: t('notifications-telegram') },
    { key: 'sms_notification', label: t('notifications-sms') },
  ];

  const toggleChannel = async (channel: PushChannel, checked: boolean) => {
    const previous = current;
    const next = { ...current, [channel]: checked };

    setDraft(next);
    selectionChanged();

    try {
      await savePushSettings(next);
      setDraft(null);
    } catch {
      // Текст ошибки показывает сам мутатор — здесь только возвращаем UI.
      setDraft(previous);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4">
      <div className="flex flex-col space-y-7">
        <p className="text-display-sm text-center">{t('settings')}</p>
        <div className="flex flex-col space-y-4">
          <Card className="flex flex-col rounded-xl px-4 py-[14px]">
            <div
              className="flex flex-row items-center justify-between"
              onClick={() => setLangOpen(true)}
            >
              <p className="text-body-base-regular text-text-primary">
                {t('language')}
              </p>
              <div className="flex flex-row items-center gap-2">
                <p className="text-body-base-regular text-text-tertiary">
                  {LANGUAGE_NAMES[lang] ?? LANGUAGE_NAMES.uz}
                </p>
                <EditChevron />
              </div>
            </div>
          </Card>

          <Card className="flex flex-col rounded-xl px-4 py-[14px]">
            <div className="flex flex-col space-y-[18px]">
              {channels.map(({ key, label }) => (
                <div
                  key={key}
                  className="flex flex-row items-center justify-between"
                >
                  <p className="text-body-base-regular text-text-primary">
                    {label}
                  </p>
                  <Switch
                    checked={current[key]}
                    // Пока профиль не загрузился, показывать «включено» и
                    // давать переключать нечестно: ответ сервера может
                    // оказаться другим, и переключатель дёрнется сам собой.
                    disabled={isLoading || isSaving}
                    onCheckedChange={checked => toggleChannel(key, checked)}
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <LanguageDrawer open={langOpen} setOpen={setLangOpen} onSaved={setLang} />
    </div>
  );
};

export default Settings;
